import { NgComponentOutlet, NgTemplateOutlet } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  viewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { L10N_LOCALE, L10nTranslatePipe, L10nTranslationService } from 'angular-l10n';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { createDashboardWidgetPort, widgetInjector } from '../widget/dashboard-widget-handle';
import { hasSettings } from '../widget/dashboard-widget-settings';
import { DashboardWidgetInstance } from '../layout/dashboard-layout';
import { DashboardWidgetDefinition, UNAVAILABLE_WIDGET_TITLE } from '../widget/dashboard-widget-definition';
import { lazyComponent } from './lazy-component';
import { hidesBody, loadingIndicatorFor, showsBody, widgetFrameView } from './widget-frame-view';

const MOVE_KEYS: Partial<Record<string, { dx: number; dy: number }>> = {
  ArrowLeft: { dx: -1, dy: 0 },
  ArrowRight: { dx: 1, dy: 0 },
  ArrowUp: { dx: 0, dy: -1 },
  ArrowDown: { dx: 0, dy: 1 },
};

@Component({
  selector: 'app-dashboard-widget-frame',
  imports: [
    NgComponentOutlet,
    NgTemplateOutlet,
    L10nTranslatePipe,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './dashboard-widget-frame.component.html',
  styleUrls: ['./dashboard-widget-frame.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DashboardWidgetFrameComponent {
  readonly instance = input.required<DashboardWidgetInstance>();
  /** undefined when the layout refers to a type that isn't registered */
  readonly definition = input.required<DashboardWidgetDefinition | undefined>();
  readonly editing = input(false);

  readonly removeRequested = output<void>();
  readonly settingsRequested = output<void>();
  readonly moveRequested = output<{ dx: number; dy: number }>();
  readonly resizeRequested = output<{ dw: number; dh: number }>();

  private readonly injector = inject(Injector);
  private readonly translation = inject(L10nTranslationService);
  protected readonly locale = inject(L10N_LOCALE);

  private readonly dragHandle = viewChild<string, ElementRef<HTMLButtonElement>>('dragHandle', { read: ElementRef });

  protected readonly body = lazyComponent(() => {
    const definition = this.definition();
    return definition && { load: definition.loadComponent, label: `widget type '${definition.type}'` };
  });

  private readonly handle = computed(() => {
    const definition = this.definition();
    return definition ? createDashboardWidgetPort(definition, this.instance, this.editing) : null;
  });

  protected readonly bodyInjector = computed(() => {
    const handle = this.handle();
    return handle ? widgetInjector(handle, this.injector) : this.injector;
  });

  protected readonly view = computed(() => {
    const handle = this.handle();
    return widgetFrameView({
      registered: !!handle,
      code: this.body.state().status,
      data: handle?.dataState() ?? { status: 'ready' },
      hasShownData: handle?.hasShownData() ?? false,
    });
  });
  protected readonly bodyHidden = computed(() => hidesBody(this.view()));
  /** A bare widget draws its own card, so while its body is not on screen the frame stands in with one */
  protected readonly placeholderCard = computed(() => !this.isCard() && !showsBody(this.view()));
  protected readonly indicatorShape = computed(() => loadingIndicatorFor(this.instance().h));

  protected readonly title = computed(() =>
    this.translation.translate(this.definition()?.title ?? UNAVAILABLE_WIDGET_TITLE),
  );
  protected readonly subtitle = computed(() => this.handle()?.subtitle() ?? null);
  protected readonly headerActions = computed(() => this.handle()?.headerActions() ?? null);
  protected readonly isCard = computed(() => (this.definition()?.frame ?? 'card') === 'card');
  protected readonly hasSettings = computed(() => {
    const definition = this.definition();
    return !!definition && hasSettings(definition);
  });
  protected readonly dragHandleLabel = computed(() =>
    this.translation.translate('Dashboard.Frame.MoveOrResize', { title: this.title() }),
  );

  dragHandleHasFocus(): boolean {
    const handle = this.dragHandle()?.nativeElement;
    return !!handle && document.activeElement === handle;
  }

  focusDragHandle(): void {
    afterNextRender(() => this.dragHandle()?.nativeElement.focus(), { injector: this.injector });
  }

  protected onDragHandleKeydown(event: KeyboardEvent): void {
    const delta = MOVE_KEYS[event.key];
    if (!delta) {
      return;
    }

    event.preventDefault();
    if (event.shiftKey) {
      this.resizeRequested.emit({ dw: delta.dx, dh: delta.dy });
    } else {
      this.moveRequested.emit(delta);
    }
  }
}
